import React, { useContext, useState } from "react";
import { useNavigate } from "react-router-dom";

import { makeStyles } from "@mui/styles";
import { IconButton } from "@mui/material";
import { MoreVert, Replay } from "@mui/icons-material";

import { i18n } from "../../translate/i18n";
import api from "../../services/api";
import TicketOptionsMenu from "../TicketOptionsMenu";
import TransferTicketModal from "../TransferTicketModal";
import ResolveTicketModal from "../ResolveTicketModal";
import ButtonWithSpinner from "../ButtonWithSpinner";
import toastError from "../../errors/toastError";
import { AuthContext } from "../../context/Auth/AuthContext";
import ChatBackgroundPicker from "../ChatBackgroundPicker";
import { maybeCreateDriveFolder } from "../DriveFolderPrompt";

const useStyles = makeStyles(theme => ({
	actionButtons: {
		marginRight: 6,
		flex: "none",
		alignSelf: "center",
		marginLeft: "auto",
		"& > *": {
			margin: theme.spacing(1),
		},
	},
}));

const TicketActionButtons = ({ ticket }) => {
	const classes = useStyles();
	const navigate = useNavigate();
	const [anchorEl, setAnchorEl] = useState(null);
	const [loading, setLoading] = useState(false);
	const [transferModalOpen, setTransferModalOpen] = useState(false);
	const [resolveModalOpen, setResolveModalOpen] = useState(false);
	const ticketOptionsMenuOpen = Boolean(anchorEl);
	const { user } = useContext(AuthContext);

	const handleOpenTicketOptionsMenu = e => {
		setAnchorEl(e.currentTarget);
	};

	const handleCloseTicketOptionsMenu = e => {
		setAnchorEl(null);
	};

	const handleUpdateTicketStatus = async (e, status, userId) => {
		setLoading(true);
		try {
			await api.put(`/tickets/${ticket.id}`, {
				status: status,
				userId: userId || null,
			});

			setLoading(false);
			if (status === "open") {
				maybeCreateDriveFolder(ticket.contact);
				navigate(`/tickets/${ticket.id}`);
			} else {
				navigate("/tickets");
			}
		} catch (err) {
			setLoading(false);
			toastError(err);
		}
	};

	const handleResolveConfirm = async (message) => {
		setResolveModalOpen(false);
		setLoading(true);
		try {
			if (message) {
				await api.post(`/messages/${ticket.id}`, { body: message, fromMe: true });
			}
			await api.put(`/tickets/${ticket.id}`, { status: "closed" });
			navigate("/tickets");
		} catch (err) {
			toastError(err);
		} finally {
			setLoading(false);
		}
	};

	return (
		<div className={classes.actionButtons}>
			<ResolveTicketModal
				open={resolveModalOpen}
				onClose={() => setResolveModalOpen(false)}
				onConfirm={handleResolveConfirm}
			/>
			<ChatBackgroundPicker />

			{ticket.status === "closed" && (
				<ButtonWithSpinner
					loading={loading}
					startIcon={<Replay />}
					size="small"
					onClick={e => handleUpdateTicketStatus(e, "open", user?.id)}
				>
					{i18n.t("messagesList.header.buttons.reopen")}
				</ButtonWithSpinner>
			)}

			{ticket.status === "open" && (
				<>
					<ButtonWithSpinner
						loading={loading}
						startIcon={<Replay />}
						size="small"
						onClick={e => handleUpdateTicketStatus(e, "pending", null)}
					>
						{i18n.t("messagesList.header.buttons.return")}
					</ButtonWithSpinner>
					<ButtonWithSpinner
						loading={loading}
						size="small"
						variant="contained"
						color="primary"
						onClick={() => setResolveModalOpen(true)}
					>
						{i18n.t("messagesList.header.buttons.resolve")}
					</ButtonWithSpinner>
				</>
			)}

			{ticket.status === "pending" && (
				<>
					<TransferTicketModal
						modalOpen={transferModalOpen}
						onClose={() => setTransferModalOpen(false)}
						ticketid={ticket.id}
						ticketWhatsappId={ticket.whatsappId}
						ticketUserId={ticket.userId}
					/>
					<ButtonWithSpinner
						loading={loading}
						size="small"
						variant="contained"
						color="primary"
						onClick={e => handleUpdateTicketStatus(e, "open", user?.id)}
					>
						{i18n.t("messagesList.header.buttons.accept")}
					</ButtonWithSpinner>
					<ButtonWithSpinner
						loading={loading}
						size="small"
						variant="outlined"
						onClick={() => setTransferModalOpen(true)}
					>
						Transferir
					</ButtonWithSpinner>
					<ButtonWithSpinner
						loading={loading}
						size="small"
						variant="outlined"
						color="secondary"
						onClick={() => setResolveModalOpen(true)}
					>
						Resolver
					</ButtonWithSpinner>
				</>
			)}

			<IconButton onClick={handleOpenTicketOptionsMenu}>
				<MoreVert />
			</IconButton>
			<TicketOptionsMenu
				ticket={ticket}
				anchorEl={anchorEl}
				menuOpen={ticketOptionsMenuOpen}
				handleClose={handleCloseTicketOptionsMenu}
			/>
		</div>
	);
};

export default TicketActionButtons;
